import React, { useEffect, useState } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { PieChart, Pie, Cell } from 'recharts';
import WidgetCard from './WidgetCard';
import MainCardTitle from './MainCardTitle';
import CenteredSpinner from '../../../components/CenteredSpinner';
import DashboardServices from '../../../services/DashboardServices';
import localStorageService from '../../../services/localStorageService';

// Color palette for reporting type segments
const COLORS = [
    '#ec407a', // pink
    '#66bb6a', // light green
    '#ffa726', // orange
    '#ff7043', // deep orange
    '#ab47bc', // purple
    '#42a5f5', // blue
    '#8d6e63', // brown
    '#78909c', // blue grey
    '#9ccc65', // lime
    '#7e57c2', // deep purple
    '#5e35b1', // purple
    '#7cb342', // green
    '#ef5350', // red
    '#d4a017', // gold
    '#26a69a', // teal

];

const LegendItem = ({ color, label, value, percentage }) => (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Box
            sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: color,
                flexShrink: 0
            }}
        />
        <Typography variant="body2" sx={{ flex: 1, color: 'text.primary', fontSize: 13 }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: 13 }}>
            {value} ({percentage}%)
        </Typography>
    </Stack>
);

const CasesByReportingTypeChart = ({ title = "Cases by Reporting Type" }) => {
    const [loading, setLoading] = useState(true);
    const [chartData, setChartData] = useState([]);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoading(true);
            try {
                const storedUser = localStorageService?.getItem?.('user') ?? null;
                const roles = (storedUser?.roles) || (storedUser?.role) || storedUser?.rolesArray || [];
                const normalizedRoles = Array.isArray(roles) ? roles.map(r => String(r).toLowerCase()) : [];

                const targetRoles = ['organization admin', 'case officer', 'case manager', 'organization head','director cid', 'director ncpa', 'deputy director','oic','department head','team leader first respondent','call center officer mowca', 'counselling officer', 'legal officer'];
                const hasTargetRole = normalizedRoles.some(r => targetRoles.includes(r));

                const params = {};

                if (hasTargetRole) {
                    const orgId = storedUser?.employee?.organization_id ?? storedUser?.organization_id ?? storedUser?.org?.id ?? null;
                    if (orgId) params.organization_id = orgId;
                }

                const res = await DashboardServices.getDashboardCasesByReportingType(params);
                const payload = res?.data ?? res;
                const dataArray = payload?.view?.data ?? payload?.data ?? [];

                // Sort by case_count descending
                const sorted = Array.isArray(dataArray)
                    ? [...dataArray].sort((a, b) => (Number(b.case_count) || 0) - (Number(a.case_count) || 0))
                    : [];

                // Transform data for chart
                const formatted = sorted.map((item, index) => ({
                    reporting_type: item.reporting_type ?? '',
                    name: item.reporting_type_name ?? `Type ${index + 1}`,
                    value: Number(item.case_count) || 0,
                    percentage: Number(item.percentage) || 0,
                    fill: COLORS[index % COLORS.length]
                }));

                if (mounted) setChartData(formatted);
            } catch (err) {
                console.error('Failed to load cases by reporting type', err);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        load();
        return () => { mounted = false; };
    }, []);

   // if (loading) return <WidgetCard sx={{ minHeight: 250 }}><CenteredSpinner /></WidgetCard>;

    return (
        <WidgetCard sx={{ minHeight: 250 }}>
            <MainCardTitle>{title}</MainCardTitle>

            {chartData.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                    No data available
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', gap: 4, mt: 2, alignItems: 'center' }}>
                    {/* Chart */}
                    <Box sx={{ height: 180, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <PieChart width={280} height={180}>
                            <Pie
                                data={chartData}
                                //cx={110}
                                // cy={110}
                                innerRadius={50}
                                outerRadius={80}
                                paddingAngle={5}
                                dataKey="value"
                                cornerRadius={10}
                            >
                                {chartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.fill} />
                                ))}
                            </Pie>
                        </PieChart>
                    </Box>

                    {/* Legend */}
                    <Box sx={{ flex: 1, maxHeight: 250, overflowY: 'auto', pr: 1 }}>
                        {chartData.map((item, index) => (
                            <LegendItem
                                key={index}
                                color={item.fill}
                                label={item.name}
                                value={item.value}
                                percentage={item.percentage}
                            />
                        ))}
                    </Box>
                </Box>
            )}
        </WidgetCard>
    );
};

export default CasesByReportingTypeChart;
